import React from 'react';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useCriteriaManagement, { DIALOG_TYPE } from './hooks/useCriteriaManagement';
import CriteriaFormDialog from './components/CriteriaFormDialog';

const WarrantyAuditCriteriaManagementPage = () => {
    const { th } = useLanguage();
    const { criteria, isLoading, isSaving, dialog, openCreate, openEdit, closeDialog, handleSave, handleDelete } =
        useCriteriaManagement();

    const confirmDelete = (type, id, message) => {
        confirmDialog({
            message: message || th('warrantyAudit.confirm.deleteMessage') || 'Bu kayıt pasife alınacaktır. Devam edilsin mi?',
            header: th('warrantyAudit.confirm.deleteHeader') || 'Silme Onayı',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: th('warrantyAudit.actions.delete') || 'Sil',
            rejectLabel: th('warrantyAudit.actions.cancel') || 'İptal',
            acceptClassName: 'p-button-danger',
            accept: () => handleDelete(type, id)
        });
    };

    const inactiveTag = (isActive) =>
        isActive === 1 || isActive === true ? null : (
            <Tag severity="warning" value={th('warrantyAudit.fields.inactive') || 'Pasif'} className="ml-2" />
        );

    const evaluationActions = (rowData, subCriteriaId) => (
        <div className="flex gap-2 justify-content-center">
            <Button
                icon="pi pi-pencil"
                rounded
                text
                severity="info"
                tooltip={th('warrantyAudit.actions.edit') || 'Düzenle'}
                onClick={() => openEdit(DIALOG_TYPE.EVALUATION, rowData, subCriteriaId)}
            />
            <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                tooltip={th('warrantyAudit.actions.delete') || 'Sil'}
                onClick={() => confirmDelete(DIALOG_TYPE.EVALUATION, rowData.id)}
            />
        </div>
    );

    const renderEvaluations = (subCriteria) => (
        <DataTable
            value={subCriteria.evaluations || []}
            size="small"
            className="mt-2"
            emptyMessage={th('warrantyAudit.evaluation.empty') || 'Değerlendirme yok'}
            rowClassName={(row) => (row.isActive === 1 || row.isActive === true ? '' : 'text-color-secondary')}
        >
            <Column field="sort" header={th('warrantyAudit.fields.sort') || 'Sıralama'} style={{ width: '90px' }} />
            <Column field="criterionText" header={th('warrantyAudit.evaluation.criterionText') || 'Değerlendirme Kriter'} />
            <Column field="evaluationText" header={th('warrantyAudit.evaluation.evaluationText') || 'Değerlendirme'} />
            <Column field="point" header={th('warrantyAudit.fields.point') || 'Puan'} style={{ width: '90px' }} />
            <Column
                header={th('warrantyAudit.fields.status') || 'Durum'}
                style={{ width: '90px' }}
                body={(row) => (row.isActive === 1 || row.isActive === true ? <Tag severity="success" value={th('warrantyAudit.fields.active') || 'Aktif'} /> : <Tag severity="warning" value={th('warrantyAudit.fields.inactive') || 'Pasif'} />)}
            />
            <Column
                header={th('warrantyAudit.fields.actions') || 'İşlemler'}
                style={{ width: '120px' }}
                body={(row) => evaluationActions(row, subCriteria.id)}
            />
        </DataTable>
    );

    const renderSubCriteria = (subCriteria) => (
        <div className="surface-card border-round p-3 mb-3" key={subCriteria.id}>
            <div className="flex justify-content-between align-items-center flex-wrap gap-2">
                <div className="flex align-items-center gap-2">
                    {subCriteria.displayCode && <Tag value={subCriteria.displayCode} />}
                    <span className="font-medium">{subCriteria.name}</span>
                    {subCriteria.maxScore != null && (
                        <span className="text-sm text-color-secondary">
                            ({th('warrantyAudit.fields.maxScore') || 'Maks. Puan'}: {subCriteria.maxScore})
                        </span>
                    )}
                    {inactiveTag(subCriteria.isActive)}
                </div>
                <div className="flex gap-2">
                    <Button
                        icon="pi pi-plus"
                        label={th('warrantyAudit.evaluation.add') || 'Değerlendirme Ekle'}
                        size="small"
                        outlined
                        onClick={() => openCreate(DIALOG_TYPE.EVALUATION, subCriteria.id)}
                    />
                    <Button
                        icon="pi pi-pencil"
                        size="small"
                        text
                        severity="info"
                        tooltip={th('warrantyAudit.actions.edit') || 'Düzenle'}
                        onClick={() => openEdit(DIALOG_TYPE.SUB_CRITERIA, subCriteria, subCriteria.criteriaId)}
                    />
                    <Button
                        icon="pi pi-trash"
                        size="small"
                        text
                        severity="danger"
                        tooltip={th('warrantyAudit.actions.delete') || 'Sil'}
                        onClick={() => confirmDelete(DIALOG_TYPE.SUB_CRITERIA, subCriteria.id, th('warrantyAudit.confirm.deleteSubMessage'))}
                    />
                </div>
            </div>
            {renderEvaluations(subCriteria)}
        </div>
    );

    const criteriaHeaderTemplate = (criterion) => (
        <div className="flex justify-content-between align-items-center w-full flex-wrap gap-2">
            <div className="flex align-items-center gap-2">
                <Tag value={criterion.sort} rounded />
                <span className="font-semibold">{criterion.name}</span>
                {inactiveTag(criterion.isActive)}
            </div>
            <div className="flex gap-2">
                <Button
                    icon="pi pi-plus"
                    label={th('warrantyAudit.subCriteria.add') || 'Alt Kriter Ekle'}
                    size="small"
                    outlined
                    onClick={(e) => {
                        e.stopPropagation();
                        openCreate(DIALOG_TYPE.SUB_CRITERIA, criterion.id);
                    }}
                />
                <Button
                    icon="pi pi-pencil"
                    size="small"
                    text
                    severity="info"
                    tooltip={th('warrantyAudit.actions.edit') || 'Düzenle'}
                    onClick={(e) => {
                        e.stopPropagation();
                        openEdit(DIALOG_TYPE.CRITERIA, criterion);
                    }}
                />
                <Button
                    icon="pi pi-trash"
                    size="small"
                    text
                    severity="danger"
                    tooltip={th('warrantyAudit.actions.delete') || 'Sil'}
                    onClick={(e) => {
                        e.stopPropagation();
                        confirmDelete(DIALOG_TYPE.CRITERIA, criterion.id, th('warrantyAudit.confirm.deleteCriteriaMessage'));
                    }}
                />
            </div>
        </div>
    );

    return (
        <div className="card">
            <ConfirmDialog />
            <div className="flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <h1 className="m-0">{th('warrantyAudit.criteriaManagement.pageTitle') || 'Denetim Maddeleri Tanımları'}</h1>
                <Button
                    label={th('warrantyAudit.criteria.add') || 'Kriter Ekle'}
                    icon="pi pi-plus"
                    onClick={() => openCreate(DIALOG_TYPE.CRITERIA)}
                />
            </div>

            {isLoading ? (
                <div className="flex justify-content-center p-5">
                    <ProgressSpinner />
                </div>
            ) : criteria.length === 0 ? (
                <p className="text-color-secondary">{th('warrantyAudit.criteria.empty') || 'Tanımlı kriter bulunmuyor.'}</p>
            ) : (
                criteria.map((criterion) => (
                    <Panel
                        key={criterion.id}
                        className="mb-3"
                        toggleable
                        headerTemplate={(options) => (
                            <div className="p-panel-header flex align-items-center" onClick={options.onTogglerClick} style={{ cursor: 'pointer' }}>
                                <button className={options.togglerClassName} onClick={options.onTogglerClick} type="button">
                                    <span className={options.togglerIconClassName} />
                                </button>
                                <div className="flex-1 ml-2">{criteriaHeaderTemplate(criterion)}</div>
                            </div>
                        )}
                    >
                        {(criterion.subCriteria || []).length === 0 ? (
                            <p className="text-color-secondary m-0">{th('warrantyAudit.subCriteria.empty') || 'Alt kriter yok.'}</p>
                        ) : (
                            (criterion.subCriteria || []).map(renderSubCriteria)
                        )}
                    </Panel>
                ))
            )}

            {dialog.visible && (
                <CriteriaFormDialog
                    visible={dialog.visible}
                    type={dialog.type}
                    mode={dialog.mode}
                    data={dialog.data}
                    parentId={dialog.parentId}
                    onHide={closeDialog}
                    onSubmit={handleSave}
                    isSaving={isSaving}
                    th={th}
                />
            )}
        </div>
    );
};

export default WarrantyAuditCriteriaManagementPage;
